import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";

import api from "../Component/Api";
import {
  getCurrentBusiness,
  updateBusinessProfile,
} from "../Redux/Asycthunk";

import "../Styles/Profile.css";

export default function Profile() {
  const business = useSelector((state) => state.auth.business);
  const dispatch = useDispatch();

  const [editing, setEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState({
    businessName: "",
    about: "",
    logoUrl: null,
    preview: null,
    instagram: "",
    tiktok: "",
    facebook: "",
    whatsappNumber: "",
    website: "",
    google: "",
    x: "",
    linkedin: "",
    snapchat: "",
    youtube: "",
    email: "",
  });

  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
  });

  useEffect(() => {
    if (business) {
      setDraft({
        businessName: business?.businessName || "",
        about: business?.about || "",
        logoUrl: null,
        preview: null,

        instagram: business?.socialLinks?.instagram || "",
        tiktok: business?.socialLinks?.tiktok || "",
        facebook: business?.socialLinks?.facebook || "",
        whatsappNumber: business?.whatsappNumber || "",
        website: business?.website || "",
        google: business?.reviewLinks?.google || "",
        x: business?.socialLinks?.x || "",
        linkedin: business?.socialLinks?.linkedin || "",
        snapchat: business?.socialLinks?.snapchat || "",
        youtube: business?.socialLinks?.youtube || "",
        email: business?.email || "",
      });
    }
  }, [business]);

  const startEditing = () => {
    setDraft({
      businessName: business?.businessName || "",
      about: business?.about || "",
      logoUrl: null,
      preview: null,

      instagram: business?.socialLinks?.instagram || "",
      tiktok: business?.socialLinks?.tiktok || "",
      facebook: business?.socialLinks?.facebook || "",
      whatsappNumber: business?.whatsappNumber || "",
      website: business?.website || "",
      google: business?.reviewLinks?.google || "",
      x: business?.socialLinks?.x || "",
      linkedin: business?.socialLinks?.linkedin || "",
      snapchat: business?.socialLinks?.snapchat || "",
      youtube: business?.socialLinks?.youtube || "",
      email: business?.email || "",
    });

    setEditing(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
      });

      if (compressedFile.size > 2 * 1024 * 1024) {
        toast.error("Unable to compress image below 2MB.");
        e.target.value = "";
        return;
      }

      setDraft((prev) => ({
        ...prev,
        logoUrl: compressedFile,
        preview: URL.createObjectURL(compressedFile),
      }));
    } catch (err) {
      toast.error("Unable to process image.");
      e.target.value = "";
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("businessName", draft.businessName);
      formData.append("about", draft.about);

      formData.append("instagram", draft.instagram);
      formData.append("tiktok", draft.tiktok);
      formData.append("facebook", draft.facebook);
      formData.append("whatsappNumber", draft.whatsappNumber);
      formData.append("website", draft.website);
      formData.append("googleReviewLink", draft.google);
      formData.append("x", draft.x);
      formData.append("linkedin", draft.linkedin);
      formData.append("snapchat", draft.snapchat);
      formData.append("youtube", draft.youtube);
      formData.append("email", draft.email);

      if (draft.logoUrl) {
        formData.append("logoUrl", draft.logoUrl);
      }

      const res = await dispatch(
        updateBusinessProfile(formData)
      ).unwrap();

      await dispatch(getCurrentBusiness());

      toast.success(res.message || "Business updated");

      setEditing(false);
    } catch (err) {
      toast.error("Failed to update business");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    try {
      const res = await api.patch("/change-password", {
        oldPassword: passwords.current,
        newPassword: passwords.new,
      });

      toast.success(
        res.data.message || "Password updated successfully"
      );

      if (res.data.message === "Password changed successfully") {
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 2000);
      }

      setPasswords({
        current: "",
        new: "",
      });
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Failed to change password"
      );
    }
  };

  const deleteAccount = () => {
    alert("Account deleted (mock)");
  };

  const getInitials = () => {
    return (
      business?.businessName
        ?.split(" ")
        .map((word) => word[0])
        .slice(0, 2)
        .join("")
        .toUpperCase() || "B"
    );
  };

  const socialPlatforms = [
    {
      name: "Instagram",
      field: "instagram",
      placeholder: "@username",
      icon: "◎",
    },
    {
      name: "TikTok",
      field: "tiktok",
      placeholder: "@username",
      icon: "♪",
    },
    {
      name: "Facebook",
      field: "facebook",
      placeholder: "Facebook profile",
      icon: "f",
    },
    {
      name: "X",
      field: "x",
      placeholder: "@username",
      icon: "𝕏",
    },
    {
      name: "LinkedIn",
      field: "linkedin",
      placeholder: "LinkedIn profile",
      icon: "in",
    },
    {
      name: "Snapchat",
      field: "snapchat",
      placeholder: "Username",
      icon: "👻",
    },
    {
      name: "YouTube",
      field: "youtube",
      placeholder: "Channel",
      icon: "▶",
    },
  ];

  return (
    <div className="profile-page">

      {/* ================= HEADER ================= */}

      <div className="settings-header">
        <div>
          <span className="settings-eyebrow">
            PROFILE & SETTINGS
          </span>

          <h1>Business profile</h1>

          <p>
            Manage how your business appears to customers.
          </p>
        </div>
      </div>

      {/* ================= PROFILE CARD ================= */}

      <section className="profile-card profile-main-card">

        <div className="profile-cover"></div>

        <div className="profile-main-content">

          <div className="profile-identity">

            <div className="profile-avatar-large">
              {business?.logoUrl ? (
                <img
                  src={business?.logoUrl}
                  alt={
                    business?.businessName ||
                    "Business logo"
                  }
                />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>

            <div className="profile-identity-content">

              <h2>
                {business?.businessName ||
                  business?.username ||
                  "Your business"}
              </h2>

              <p>
                {business?.about ||
                  "Add a short description about your business."}
              </p>

              {business?.businessLink && (
                <a
                  href={business.businessLink}
                  target="_blank"
                  rel="noreferrer"
                  className="customer-page-link"
                >
                  View customer page
                  <span>↗</span>
                </a>
              )}

            </div>

          </div>

          {!editing && (
            <button
              className="edit-profile-button"
              onClick={startEditing}
            >
              Edit profile
            </button>
          )}

        </div>

      </section>

      {/* ================= EDIT PROFILE ================= */}

      {editing && (
        <div className="profile-edit-wrapper">

          {/* PROFILE INFORMATION */}

          <section className="profile-card settings-section">

            <div className="settings-section-header">
              <div>
                <span className="section-eyebrow">
                  BUSINESS
                </span>

                <h3>Profile information</h3>

                <p>
                  Keep your business information clear and
                  up to date.
                </p>
              </div>
            </div>

            {/* LOGO */}

            <div className="logo-upload">

              <div className="logo-preview">
                {draft.preview ? (
                  <img
                    src={draft.preview}
                    alt="Logo preview"
                  />
                ) : business?.logoUrl ? (
                  <img
                    src={business.logoUrl}
                    alt="Business logo"
                  />
                ) : (
                  <span>{getInitials()}</span>
                )}
              </div>

              <div className="logo-upload-content">

                <strong>Business logo</strong>

                <p>
                  Use a clear square image. PNG or JPG,
                  up to 2MB.
                </p>

                <label className="upload-button">
                  Choose image

                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                  />
                </label>

              </div>

            </div>

            {/* ABOUT */}

            <div className="form-group">

              <label>About your business</label>

              <textarea
                name="about"
                value={draft.about}
                onChange={(e) => {
                  if (e.target.value.length <= 120) {
                    handleChange(e);
                  }
                }}
                placeholder="Tell customers what you do in 1–2 lines"
              />

              <div className="character-count">
                {draft.about?.length || 0}/120
              </div>

            </div>

          </section>

          {/* ================= SOCIAL ================= */}

          <section className="profile-card settings-section">

            <div className="settings-section-header">

              <div>
                <span className="section-eyebrow">
                  CONNECTIONS
                </span>

                <h3>Social media</h3>

                <p>
                  Choose where customers can find you
                  online.
                </p>
              </div>

            </div>

            <div className="social-grid">

              {socialPlatforms.map((platform) => (
                <div
                  className="social-input-card"
                  key={platform.field}
                >

                  <div className="social-icon">
                    {platform.icon}
                  </div>

                  <div className="social-input">

                    <label>{platform.name}</label>

                    <input
                      name={platform.field}
                      value={draft[platform.field]}
                      onChange={handleChange}
                      placeholder={platform.placeholder}
                    />

                  </div>

                </div>
              ))}

            </div>

          </section>

          {/* ================= REVIEWS ================= */}

          <section className="profile-card settings-section">

            <div className="settings-section-header">

              <div>
                <span className="section-eyebrow">
                  TRUST
                </span>

                <h3>Customer reviews</h3>

                <p>
                  Give customers an easy way to review
                  your business.
                </p>
              </div>

            </div>

            <div className="form-group">

              <label>Google review link</label>

              <input
                name="google"
                value={draft.google}
                onChange={handleChange}
                placeholder="Paste your Google review link"
              />

            </div>

          </section>

          {/* ================= CONTACT ================= */}

          <section className="profile-card settings-section">

            <div className="settings-section-header">

              <div>
                <span className="section-eyebrow">
                  CONTACT
                </span>

                <h3>Contact & website</h3>

                <p>
                  Give customers another way to reach you.
                </p>
              </div>

            </div>

            <div className="contact-grid">

              <div className="form-group">

                <label>WhatsApp</label>

                <input
                  name="whatsappNumber"
                  value={draft.whatsappNumber}
                  onChange={handleChange}
                  placeholder="+44 7123 456789"
                  type="tel"
                  inputMode="tel"
                />

              </div>

              <div className="form-group">

                <label>Email</label>

                <input
                  name="email"
                  value={draft.email}
                  onChange={handleChange}
                  placeholder="hello@example.com"
                  type="email"
                />

              </div>

              <div className="form-group contact-full">

                <label>Website</label>

                <input
                  name="website"
                  value={draft.website}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com"
                />

              </div>

            </div>

          </section>

          {/* ================= SAVE ================= */}

          <div className="profile-save-bar">

            <button
              type="button"
              className="cancel-profile-button"
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="save-profile-button"
              onClick={saveProfile}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save changes"}
            </button>

          </div>

        </div>
      )}

      {/* ================= SECURITY ================= */}

      <section className="profile-card settings-section">

        <div className="settings-section-header">

          <div>
            <span className="section-eyebrow">
              SECURITY
            </span>

            <h3>Password</h3>

            <p>
              Keep your ClusterClear account secure.
            </p>
          </div>

        </div>

        <div className="security-form">

          <div className="form-group">

            <label>Current password</label>

            <input
              type={showPassword ? "text" : "password"}
              placeholder="Current password"
              value={passwords.current}
              onChange={(e) =>
                setPasswords({
                  ...passwords,
                  current: e.target.value,
                })
              }
            />

          </div>

          <div className="form-group">

            <label>New password</label>

            <input
              type={showPassword ? "text" : "password"}
              placeholder="New password"
              value={passwords.new}
              onChange={(e) =>
                setPasswords({
                  ...passwords,
                  new: e.target.value,
                })
              }
            />

          </div>

        </div>

        <div className="security-actions">

          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              setShowPassword((prev) => !prev)
            }
          >
            {showPassword
              ? "Hide passwords"
              : "Show passwords"}
          </button>

          <button
            className="save-profile-button"
            onClick={changePassword}
          >
            Update password
          </button>

        </div>

      </section>

      {/* ================= DANGER ================= */}

      <section className="profile-card danger-card">

        <div>

          <span className="section-eyebrow danger-eyebrow">
            DANGER ZONE
          </span>

          <h3>Delete account</h3>

          <p>
            Permanently delete your account and business
            profile. This action cannot be undone.
          </p>

        </div>

        <button
          className="delete-button"
          onClick={deleteAccount}
        >
          Delete account
        </button>

      </section>

    </div>
  );
}